/**
 * One MOU: the document, the actions that move it, and the timeline.
 *
 * The action buttons are driven by status rather than always shown, because
 * every transition here has required evidence behind it — a send records who
 * it went to, a signature records who signed and how — and offering "Mark
 * signed" on an already-signed MOU is how a record of a commitment gets
 * quietly rewritten. A signed MOU is read-only on this screen for the same
 * reason; changing the deal means cancelling it and preparing a new one.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Select } from "antd";
import {
  ArrowLeft, Ban, BadgeCheck, Check, Copy, Download, ExternalLink, Eye, FileSignature,
  FileUp, History, Link2, Loader2, Lock, Mail, MessageSquarePlus, PenLine, RefreshCw, Repeat,
  RotateCcw, Trash2, Upload, UserPlus, X,
} from "lucide-react";

import apiClient from "../../lib/apiClient";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import {
  MOU_ACTION_URL, MOU_DETAIL_URL, MOU_DOCUMENT_URL, MOU_EVENTS_URL,
  MOU_SHARE_LINK_URL, USERS_SIMPLE_URL,
} from "../../constants/api";
import { StatusBadge, fmtCurrency, fmtDate } from "./MOUList";
import MOUDocumentPanel from "./MOUDocumentPanel";
import MOUEmailPanel from "./MOUEmailPanel";
import SignaturePad from "../../components/common/SignaturePad";

const fmtDateTime = (val) =>
  val
    ? new Date(val).toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : "—";

const SIGNATURE_MODE_LABELS = {
  online:   "Signed online through the link",
  uploaded: "Signed copy on file",
  manual:   "Recorded by staff",
  legacy:   "Pre-existing client — signature not captured through this system",
};

const Row = ({ label, children }) => (
  <div className="flex items-baseline justify-between gap-3 py-1.5">
    <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">{label}</span>
    <span className="text-right text-xs font-medium text-gray-900 dark:text-white">{children}</span>
  </div>
);

const Section = ({ title, icon: Icon, right, children }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
    <div className="mb-3 flex items-center justify-between gap-2">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-white">
        {Icon && <Icon className="h-4 w-4 text-primary" />}
        {title}
      </h3>
      {right}
    </div>
    {children}
  </div>
);

export default function MOUDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetch, create, update, remove } = useApiMethods();

  const [mou, setMou]         = useState(null);
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState("");

  // Action panels. Only one is ever open — each collects the evidence its
  // transition needs, and stacking them would let somebody send and sign in
  // the same breath.
  const [panel, setPanel]     = useState("");
  const [sendForm, setSendForm] = useState({ sent_to_name: "", sent_to_email: "", sent_channel: "email" });
  const [signForm, setSignForm] = useState({ signed_by_name: "", signed_by_designation: "", signed_on: "" });
  const [reason, setReason]   = useState("");
  const [note, setNote]       = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm]       = useState({});
  const [share, setShare]     = useState(null);
  const [bodyStale, setBodyStale] = useState(false);
  const [csForm, setCsForm]   = useState({ name: "", designation: "", signature_image: "" });

  const mouFileRef    = useRef(null);
  const signedFileRef = useRef(null);

  const load = useCallback(() => {
    fetch(MOU_DETAIL_URL(id), {}, setLoading).then((res) => {
      if (res?.status === "success") {
        setMou(res.data);
        setShare(res.data.share_url ? { url: res.data.share_url, enabled: true } : null);
        setSendForm({
          sent_to_name:  res.data.sent_to_name || "",
          sent_to_email: res.data.sent_to_email || "",
          sent_channel:  res.data.sent_channel || "email",
        });
      } else {
        ToastMsg("error", res?.message || "Could not load the MOU");
      }
    });
  }, [id]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetch(USERS_SIMPLE_URL).then((res) => {
      const list = res?.data || res?.results || [];
      setUsers(list.map((u) => ({
        value: String(u.id),
        label: [u.login_fname, u.login_lname].filter(Boolean).join(" ").trim() || `#${u.id}`,
      })));
    }).catch(() => {});
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── actions ────────────────────────────────────────────────────────────────

  const act = async (action, extra = {}) => {
    setBusy(action);
    const res = await create(MOU_ACTION_URL(id), { action, ...extra });
    setBusy("");
    if (res?.status === "success") {
      ToastMsg("success", res.message || "Done");
      setPanel(""); setReason("");
      if (action === "renew" && res.id) { navigate(`/mou/${res.id}`); return; }
      load();
    } else if (action === "renew" && res?.mou_id) {
      ToastMsg("info", res.message);
      navigate(`/mou/${res.mou_id}`);
    } else {
      ToastMsg("error", res?.message || "That did not work");
    }
  };

  const saveEdits = async () => {
    setBusy("save");
    const res = await update(MOU_DETAIL_URL(id), form);
    setBusy("");
    if (res?.status === "success") {
      ToastMsg("success", "Saved");
      setEditing(false);
      setBodyStale(!!res.body_may_be_stale);
      load();
    } else {
      ToastMsg("error", res?.message || "Failed to save");
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy("note");
    const res = await create(MOU_EVENTS_URL(id), { note });
    setBusy("");
    if (res?.status === "success") { setNote(""); load(); }
    else ToastMsg("error", res?.message || "Failed to add the note");
  };

  const issueLink = async (rotate = false) => {
    setBusy("link");
    const res = await create(MOU_SHARE_LINK_URL(id), { rotate });
    setBusy("");
    if (res?.status === "success") {
      setShare(res.data);
      ToastMsg("success", res.message || "Link ready");
      load();
    } else ToastMsg("error", res?.message || "Failed to create the link");
  };

  const revokeLink = async () => {
    setBusy("link");
    const res = await remove(MOU_SHARE_LINK_URL(id));
    setBusy("");
    if (res?.status === "success") { setShare(null); ToastMsg("success", "Link revoked"); load(); }
    else ToastMsg("error", res?.message || "Failed to revoke the link");
  };

  const copyLink = () => {
    if (!share?.url) return;
    navigator.clipboard?.writeText(share.url)
      .then(() => ToastMsg("success", "Link copied"))
      .catch(() => ToastMsg("error", "Could not copy — select the link and copy it manually"));
  };

  const upload = async (kind, file) => {
    if (!file) return;
    setBusy(`upload-${kind}`);
    const fd = new FormData();
    fd.append("file", file);
    const res = await create(MOU_DOCUMENT_URL(id, kind), fd, true);
    setBusy("");
    if (res?.status === "success") { ToastMsg("success", "Document uploaded"); load(); }
    else ToastMsg("error", res?.message || "Failed to upload the document");
  };

  const download = async (kind) => {
    setBusy(`download-${kind}`);
    try {
      // The response interceptor hands back response.data, so this IS the Blob.
      const data = await apiClient.get(MOU_DOCUMENT_URL(id, kind), { responseType: "blob" });
      const blob = data instanceof Blob ? data : new Blob([data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = (kind === "signed" ? mou.signed_document_name : mou.document_name)
                   || `${mou.mou_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      ToastMsg("error", "Could not download the document");
    } finally {
      setBusy("");
    }
  };

  const detachDoc = async (kind) => {
    setBusy(`detach-${kind}`);
    const res = await remove(`${MOU_DOCUMENT_URL(id, kind)}`);
    setBusy("");
    if (res?.status === "success") { ToastMsg("success", "Document removed"); load(); }
    else ToastMsg("error", res?.message || "Failed to remove the document");
  };

  // ── render ─────────────────────────────────────────────────────────────────

  if (loading && !mou) {
    return (
      <ContainerCard>
        <div className="flex h-64 items-center justify-center gap-2 text-sm text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      </ContainerCard>
    );
  }
  if (!mou) {
    return (
      <ContainerCard>
        <div className="flex h-64 flex-col items-center justify-center gap-3 text-gray-400">
          <FileSignature className="h-10 w-10 opacity-30" />
          <p className="text-sm">This MOU could not be found</p>
          <Button variant="outline" size="sm" onClick={() => navigate("/mou")}>Back to MOU list</Button>
        </div>
      </ContainerCard>
    );
  }

  const isSigned    = mou.status === "signed";
  const canSend     = mou.status === "draft" || mou.status === "sent";
  const canReopen   = ["declined", "cancelled", "expired"].includes(mou.status);
  const canShare    = mou.status === "draft" || mou.status === "sent";

  /* Closing an MOU out — signing, declining, cancelling, reopening — can be
     restricted to named MOU managers. `can_close` is the server's answer and
     the only thing worth trusting; the button is hidden rather than disabled
     when the user cannot, with a line saying who to ask, because a dead
     control with no explanation is what generates the support ticket. */
  const canClose    = mou.can_close !== false;
  const statusAllowsSign =
    ["draft", "sent", "declined", "expired"].includes(mou.status);
  const canCountersign = isSigned && !mou.countersigned_at && canClose;
  const canSign     = statusAllowsSign && canClose;
  const managerNames = (mou.managers || []).map((m) => m.name).filter(Boolean);

  return (
    <ContainerCard>
      <div className="space-y-4 p-2">

        {/* ── header ─────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <Button variant="ghost" size="icon" onClick={() => navigate("/mou")} title="Back">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {mou.org_name}
                </h1>
                <StatusBadge status={mou.status} label={mou.status_label} />
              </div>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {mou.mou_number} · {mou.title}
                {[mou.city, mou.state].filter(Boolean).length
                  ? ` · ${[mou.city, mou.state].filter(Boolean).join(", ")}`
                  : ""}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {canSend && (
              <Button size="sm" onClick={() => setPanel(panel === "email" ? "" : "email")}>
                <Mail className="mr-1 h-3.5 w-3.5" />
                {mou.sent_at ? "Re-send" : "Send to client"}
              </Button>
            )}
            {canCountersign && (
              <Button size="sm" variant="outline" onClick={() => {
                setCsForm({
                  name: mou.config?.company_signatory_name || "",
                  designation: mou.config?.company_signatory_designation || "",
                  signature_image: "",
                });
                setPanel(panel === "countersign" ? "" : "countersign");
              }}>
                <PenLine className="mr-1 h-3.5 w-3.5" /> Countersign
              </Button>
            )}
            {isSigned && (
              <Button size="sm" variant="outline" disabled={busy === "renew"}
                      onClick={() => {
                        if (window.confirm(`Prepare a renewal MOU for ${mou.org_name}, copied from ${mou.mou_number}?`)) act("renew");
                      }}>
                <Repeat className="mr-1 h-3.5 w-3.5" /> Renew
              </Button>
            )}
            {canSign && (
              <Button size="sm" variant="outline"
                      onClick={() => setPanel(panel === "sign" ? "" : "sign")}>
                <BadgeCheck className="mr-1 h-3.5 w-3.5" /> Mark signed
              </Button>
            )}
            {isSigned && !mou.onboarded_id && mou.customer_id && (
              <Button size="sm"
                      onClick={() => navigate(`/customers/customerdetails?id=${mou.customer_id}`)}>
                <UserPlus className="mr-1 h-3.5 w-3.5" /> Onboard client
              </Button>
            )}
            {mou.onboarded_id && (
              <Button size="sm" variant="outline"
                      onClick={() => navigate(`/onboarded/${mou.onboarded_id}`)}>
                <ExternalLink className="mr-1 h-3.5 w-3.5" /> View client
              </Button>
            )}
            <Button variant="outline" size="icon" onClick={load} title="Refresh">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Why the closing actions are missing, when they are. Said once, at
            the top, rather than as a tooltip on each absent button. */}
        {!isSigned && !canClose && statusAllowsSign && (
          <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800 dark:border-blue-900/50 dark:bg-blue-900/20 dark:text-blue-200">
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              You can prepare, edit, send and chase this MOU, but only an MOU manager can
              record the signature or close it out.{" "}
              {managerNames.length
                ? <>Ask <strong>{managerNames.join(", ")}</strong>.</>
                : <>No manager has been named yet — ask a super admin.</>}
            </span>
          </div>
        )}

        {/* Signed MOUs are a record, and the screen has to say why nothing can
            be changed rather than just disabling the buttons. */}
        {isSigned && (
          <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-900/50 dark:bg-emerald-900/20 dark:text-emerald-200">
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Signed{mou.signed_by_name ? ` by ${mou.signed_by_name}` : ""} on{" "}
              {fmtDateTime(mou.signed_at)}. A signed MOU is read-only — cancel it and prepare a
              new one if the agreement has changed.
              {mou.signature_mode === "legacy" && (
                <> This record was created by the migration for a client that predates the MOU
                   stage; the signature was not captured through this system.</>
              )}
            </span>
          </div>
        )}

        {/* ── action panels ──────────────────────────────────────── */}
        {panel === "email" && (
          <MOUEmailPanel
            mou={mou}
            onSent={() => { setPanel(""); load(); }}
            onCancel={() => setPanel("")}
            onManual={() => setPanel("send")}
          />
        )}

        {panel === "countersign" && (
          <div className="space-y-3 rounded-lg border border-indigo-200 bg-indigo-50/60 p-4 dark:border-indigo-900/50 dark:bg-indigo-900/10">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              Countersign for your company
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <label className="text-xs font-medium">Signatory name <span className="text-red-500">*</span></label>
                <Input value={csForm.name} onChange={(e) => setCsForm({ ...csForm, name: e.target.value })} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Designation</label>
                <Input value={csForm.designation}
                       onChange={(e) => setCsForm({ ...csForm, designation: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Signature (optional — the typed name is used if left blank)</label>
              <div className="max-w-md">
                <SignaturePad height={130}
                              onChange={(v) => setCsForm((p) => ({ ...p, signature_image: v }))} />
              </div>
            </div>
            <p className="text-[11px] text-gray-600 dark:text-gray-400">
              A new signed PDF carrying both signatures and the audit page is generated
              {mou.signature_mode === "uploaded"
                ? " — except that the uploaded signed scan on file is kept as the document of record."
                : "."}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setPanel("")}>Cancel</Button>
              <Button size="sm" disabled={busy === "countersign" || !csForm.name.trim()}
                      onClick={() => act("countersign", csForm)}>
                {busy === "countersign" && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                Countersign
              </Button>
            </div>
          </div>
        )}

        {panel === "send" && (
          <div className="space-y-3 rounded-lg border border-blue-200 bg-blue-50/60 p-4 dark:border-blue-900/50 dark:bg-blue-900/10">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="space-y-1">
                <label className="text-xs font-medium">Send to</label>
                <Input value={sendForm.sent_to_name}
                       onChange={(e) => setSendForm({ ...sendForm, sent_to_name: e.target.value })} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Email</label>
                <Input value={sendForm.sent_to_email}
                       onChange={(e) => setSendForm({ ...sendForm, sent_to_email: e.target.value })} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">How</label>
                <Select className="w-full" value={sendForm.sent_channel}
                        onChange={(v) => setSendForm({ ...sendForm, sent_channel: v })}
                        options={[
                          { value: "email",    label: "Email" },
                          { value: "whatsapp", label: "WhatsApp" },
                          { value: "hand",     label: "In person" },
                          { value: "other",    label: "Other" },
                        ]} />
              </div>
            </div>
            <p className="text-[11px] text-gray-600 dark:text-gray-400">
              Marking it sent issues the client's signing link if there isn't one yet — sending an
              MOU with no way to read it is the usual mistake.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setPanel("")}>Cancel</Button>
              <Button size="sm" disabled={busy === "send"}
                      onClick={() => act("send", sendForm)}>
                {busy === "send" && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                Mark as sent
              </Button>
            </div>
          </div>
        )}

        {panel === "sign" && (
          <div className="space-y-3 rounded-lg border border-emerald-200 bg-emerald-50/60 p-4 dark:border-emerald-900/50 dark:bg-emerald-900/10">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="space-y-1">
                <label className="text-xs font-medium">Signed by <span className="text-red-500">*</span></label>
                <Input value={signForm.signed_by_name}
                       onChange={(e) => setSignForm({ ...signForm, signed_by_name: e.target.value })} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Designation</label>
                <Input value={signForm.signed_by_designation}
                       onChange={(e) => setSignForm({ ...signForm, signed_by_designation: e.target.value })} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Signed on</label>
                <Input type="date" value={signForm.signed_on}
                       onChange={(e) => setSignForm({ ...signForm, signed_on: e.target.value })} />
              </div>
            </div>
            <p className="text-[11px] text-gray-600 dark:text-gray-400">
              {mou.has_signed_document
                ? "The countersigned copy on file will be recorded as the evidence."
                : "Upload the countersigned copy below first if you have one — without it this is "
                  + "recorded as your assertion that it was signed."}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setPanel("")}>Cancel</Button>
              <Button size="sm" disabled={busy === "sign" || !signForm.signed_by_name.trim()}
                      onClick={() => act("sign", signForm)}>
                {busy === "sign" && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                Record signature
              </Button>
            </div>
          </div>
        )}

        {(panel === "decline" || panel === "cancel") && (
          <div className="space-y-3 rounded-lg border border-red-200 bg-red-50/60 p-4 dark:border-red-900/50 dark:bg-red-900/10">
            <div className="space-y-1">
              <label className="text-xs font-medium">
                Why {panel === "decline" ? "did the client decline" : "is this being cancelled"}?
              </label>
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => { setPanel(""); setReason(""); }}>
                Cancel
              </Button>
              <Button size="sm" disabled={busy === panel}
                      onClick={() => act(panel, { reason })}>
                {busy === panel && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                {panel === "decline" ? "Record decline" : "Cancel this MOU"}
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">

          {/* ── left: the agreement ─────────────────────────────── */}
          <div className="space-y-4 lg:col-span-2">
            <Section
              title="The agreement"
              icon={FileSignature}
              right={
                mou.can_edit ? (
                  editing ? (
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
                        Cancel
                      </Button>
                      <Button size="sm" disabled={busy === "save"} onClick={saveEdits}>
                        {busy === "save" && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                        Save
                      </Button>
                    </div>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => {
                      setForm({
                        org_name: mou.org_name, city: mou.city, state: mou.state,
                        title: mou.title, scope_summary: mou.scope_summary,
                        terms_text: mou.terms_text,
                        contract_value: mou.contract_value,
                        contract_period: mou.contract_period,
                        payment_terms: mou.payment_terms,
                        effective_date: mou.effective_date || "",
                        valid_until: mou.valid_until || "",
                        contract_end_date: mou.contract_end_date || "",
                        cc_emails: mou.cc_emails || "",
                        owner_id: mou.owner_id ? String(mou.owner_id) : "",
                        notes: mou.notes,
                      });
                      setEditing(true);
                    }}>
                      Edit
                    </Button>
                  )
                ) : null
              }
            >
              {editing ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Input placeholder="Organisation" value={form.org_name || ""}
                           onChange={(e) => setForm({ ...form, org_name: e.target.value })} />
                    <Input placeholder="Title" value={form.title || ""}
                           onChange={(e) => setForm({ ...form, title: e.target.value })} />
                    <Input placeholder="City" value={form.city || ""}
                           onChange={(e) => setForm({ ...form, city: e.target.value })} />
                    <Input placeholder="State" value={form.state || ""}
                           onChange={(e) => setForm({ ...form, state: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <Input type="number" placeholder="Value" value={form.contract_value || ""}
                           onChange={(e) => setForm({ ...form, contract_value: e.target.value })} />
                    <Input placeholder="Period" value={form.contract_period || ""}
                           onChange={(e) => setForm({ ...form, contract_period: e.target.value })} />
                    <Input placeholder="Payment terms" value={form.payment_terms || ""}
                           onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <Input type="date" value={form.effective_date || ""}
                           onChange={(e) => setForm({ ...form, effective_date: e.target.value })} />
                    <Input type="date" value={form.valid_until || ""}
                           onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
                    <Select className="w-full" placeholder="Chased by" allowClear showSearch
                            optionFilterProp="label" value={form.owner_id || undefined}
                            onChange={(v) => setForm({ ...form, owner_id: v || "" })}
                            options={users} />
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="space-y-1">
                      <label className="text-[11px] text-gray-500">Contract end date</label>
                      <Input type="date" value={form.contract_end_date || ""}
                             onChange={(e) => setForm({ ...form, contract_end_date: e.target.value })} />
                    </div>
                    <div className="space-y-1 sm:col-span-2">
                      <label className="text-[11px] text-gray-500">CC on emails</label>
                      <Input placeholder="accounts@client.com, md@client.com" value={form.cc_emails || ""}
                             onChange={(e) => setForm({ ...form, cc_emails: e.target.value })} />
                    </div>
                  </div>
                  <Textarea rows={3} placeholder="Scope" value={form.scope_summary || ""}
                            onChange={(e) => setForm({ ...form, scope_summary: e.target.value })} />
                  <Textarea rows={4} placeholder="Terms" value={form.terms_text || ""}
                            onChange={(e) => setForm({ ...form, terms_text: e.target.value })} />
                  <Textarea rows={2} placeholder="Internal notes" value={form.notes || ""}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-x-6 sm:grid-cols-3">
                    <Row label="Value">{fmtCurrency(mou.contract_value)}</Row>
                    <Row label="Period">{mou.contract_period || "—"}</Row>
                    <Row label="Payment">{mou.payment_terms || "—"}</Row>
                    <Row label="Effective">{fmtDate(mou.effective_date)}</Row>
                    <Row label="Valid until">{fmtDate(mou.valid_until)}</Row>
                    <Row label="Chased by">{mou.owner_name || "—"}</Row>
                    <Row label="Contract ends">{fmtDate(mou.contract_end_date)}</Row>
                    <Row label="CC">{mou.cc_emails || "—"}</Row>
                  </div>
                  {mou.scope_summary && (
                    <div>
                      <p className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">Scope</p>
                      <p className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200">
                        {mou.scope_summary}
                      </p>
                    </div>
                  )}
                  {mou.terms_text && (
                    <div>
                      <p className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">Terms</p>
                      <p className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200">
                        {mou.terms_text}
                      </p>
                    </div>
                  )}
                  {mou.notes && (
                    <div className="rounded-md bg-gray-50 p-3 dark:bg-gray-700/40">
                      <p className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">
                        Internal notes — never shown to the client
                      </p>
                      <p className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200">
                        {mou.notes}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </Section>

            {/* ── the generated document ────────────────────────── */}
            <MOUDocumentPanel mou={mou} onChanged={() => { setBodyStale(false); load(); }}
                              bodyStale={bodyStale} />

            {/* ── documents ─────────────────────────────────────── */}
            <Section title="Files on record" icon={FileUp}>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[
                  { kind: "mou",    label: "MOU as sent",     has: mou.has_document,
                    name: mou.document_name, ref: mouFileRef,
                    hint: "What goes to the client — the latest generated PDF, or a file you upload. Offered on their signing page." },
                  { kind: "signed", label: "Signed copy", has: mou.has_signed_document,
                    name: mou.signed_document_name, ref: signedFileRef,
                    hint: "Generated with the signatures when signed online, or the scan that comes back. Kept separately so the sent version survives." },
                ].map((d) => (
                  <div key={d.kind}
                       className="rounded-md border border-gray-200 p-3 dark:border-gray-700">
                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{d.label}</p>
                    <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">{d.hint}</p>
                    {d.has ? (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="max-w-[10rem] truncate text-xs text-gray-800 dark:text-gray-200">
                          {d.name}
                        </span>
                        <Button variant="outline" size="sm" disabled={busy === `download-${d.kind}`}
                                onClick={() => download(d.kind)}>
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                        {!(d.kind === "signed" && isSigned) && (
                          <Button variant="ghost" size="sm" disabled={busy === `detach-${d.kind}`}
                                  onClick={() => detachDoc(d.kind)} title="Remove">
                            <Trash2 className="h-3.5 w-3.5 text-red-500" />
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="mt-2">
                        <input ref={d.ref} type="file" className="hidden"
                               accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp"
                               onChange={(e) => { upload(d.kind, e.target.files?.[0]); e.target.value = ""; }} />
                        <Button variant="outline" size="sm"
                                disabled={busy === `upload-${d.kind}`}
                                onClick={() => d.ref.current?.click()}>
                          {busy === `upload-${d.kind}`
                            ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                            : <Upload className="mr-1 h-3.5 w-3.5" />}
                          Upload
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <p className="mt-3 text-[11px] text-gray-500 dark:text-gray-400">
                PDF, Word or an image, up to 10MB. Files are served through the app, not from a
                public URL.
              </p>
            </Section>

            {/* ── timeline ──────────────────────────────────────── */}
            <Section title="Timeline" icon={History}>
              <div className="mb-3 flex items-start gap-2">
                <Textarea rows={2} placeholder="Add a note…" value={note}
                          onChange={(e) => setNote(e.target.value)} />
                <Button size="sm" disabled={busy === "note" || !note.trim()} onClick={addNote}>
                  {busy === "note"
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <MessageSquarePlus className="h-3.5 w-3.5" />}
                </Button>
              </div>
              {(mou.events || []).length === 0 ? (
                <p className="text-xs text-gray-400">Nothing recorded yet</p>
              ) : (
                <ol className="space-y-3">
                  {mou.events.map((e) => (
                    <li key={e.id} className="flex gap-3">
                      <div className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <span className="text-xs font-medium text-gray-900 dark:text-white">
                            {e.label}
                          </span>
                          <span className="text-[11px] text-gray-400">{fmtDateTime(e.at)}</span>
                          {e.actor_name && (
                            <span className="text-[11px] text-gray-500 dark:text-gray-400">
                              · {e.actor_name}
                            </span>
                          )}
                        </div>
                        {e.note && (
                          <p className="mt-0.5 whitespace-pre-wrap break-words text-xs text-gray-600 dark:text-gray-300">
                            {e.note}
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </Section>
          </div>

          {/* ── right: status, link, other actions ──────────────── */}
          <div className="space-y-4">
            <Section title="Status" icon={Check}>
              <Row label="Prepared by">{mou.prepared_by_name || "—"}</Row>
              <Row label="Prepared">{fmtDate(mou.created_date)}</Row>
              <Row label="Sent">{mou.sent_at ? fmtDateTime(mou.sent_at) : "Not sent"}</Row>
              {mou.sent_to_name || mou.sent_to_email ? (
                <Row label="Sent to">
                  {mou.sent_to_name}
                  {mou.sent_to_email ? ` · ${mou.sent_to_email}` : ""}
                </Row>
              ) : null}
              <Row label="Opened by client">
                {mou.viewed_at ? fmtDateTime(mou.viewed_at)
                  : mou.sent_at ? <span className="text-amber-600 dark:text-amber-400">Not yet</span>
                  : "—"}
              </Row>
              <Row label="Signed">{mou.signed_at ? fmtDateTime(mou.signed_at) : "Not signed"}</Row>
              {mou.signed_by_name && (
                <Row label="Signed by">
                  {mou.signed_by_name}
                  {mou.signed_by_designation ? ` (${mou.signed_by_designation})` : ""}
                </Row>
              )}
              {mou.otp_verified_at && (
                <Row label="Email verified">
                  <span className="text-emerald-600 dark:text-emerald-400">
                    {mou.signed_email || "Yes"}
                  </span>
                </Row>
              )}
              {mou.signed_ip && <Row label="Signed from">{mou.signed_ip}</Row>}
              {mou.countersigned_at && (
                <Row label="Countersigned">
                  {mou.countersigned_by_name} · {fmtDateTime(mou.countersigned_at)}
                </Row>
              )}
              {mou.reminder_count > 0 && (
                <Row label="Reminders sent">
                  {mou.reminder_count} · last {fmtDateTime(mou.last_reminder_at)}
                </Row>
              )}
              {mou.signature_image && (
                <div className="mt-2 rounded-md border border-gray-200 bg-white p-2 dark:border-gray-700">
                  <img src={mou.signature_image} alt="Client signature" className="mx-auto h-14 object-contain" />
                </div>
              )}
              {mou.signature_mode && (
                <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
                  {SIGNATURE_MODE_LABELS[mou.signature_mode] || mou.signature_mode}
                  {mou.signature_type === "drawn" ? " · drawn signature" : mou.signature_type === "typed" ? " · typed name" : ""}
                </p>
              )}
              {mou.renewed_from_id && (
                <button className="mt-2 text-[11px] text-primary hover:underline"
                        onClick={() => navigate(`/mou/${mou.renewed_from_id}`)}>
                  Renewal of an earlier MOU →
                </button>
              )}
              {(mou.renewals || []).map((r) => (
                <button key={r.id} className="mt-1 block text-[11px] text-primary hover:underline"
                        onClick={() => navigate(`/mou/${r.id}`)}>
                  Renewed as {r.mou_number} ({r.status}) →
                </button>
              ))}
              {mou.declined_at && (
                <div className="mt-2 rounded-md bg-red-50 p-2 dark:bg-red-900/20">
                  <p className="text-[11px] font-medium text-red-700 dark:text-red-300">
                    Declined {fmtDateTime(mou.declined_at)}
                  </p>
                  {mou.decline_reason && (
                    <p className="mt-0.5 text-[11px] text-red-600 dark:text-red-300">
                      {mou.decline_reason}
                    </p>
                  )}
                </div>
              )}
            </Section>

            {/* The client's signing link */}
            <Section title="Client signing link" icon={Link2}>
              {canShare ? (
                share?.url ? (
                  <div className="space-y-2">
                    <div className="break-all rounded-md bg-gray-50 p-2 text-[11px] text-gray-700 dark:bg-gray-700/40 dark:text-gray-200">
                      {share.url}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" onClick={copyLink}>
                        <Copy className="mr-1 h-3.5 w-3.5" /> Copy
                      </Button>
                      <Button variant="outline" size="sm" disabled={busy === "link"}
                              onClick={() => issueLink(true)} title="Issue a new token; the old link stops working">
                        <RotateCcw className="mr-1 h-3.5 w-3.5" /> Rotate
                      </Button>
                      <Button variant="ghost" size="sm" disabled={busy === "link"}
                              onClick={revokeLink}>
                        <X className="mr-1 h-3.5 w-3.5" /> Revoke
                      </Button>
                    </div>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      Anyone holding this URL can read the commercials and sign. It is revoked
                      automatically the moment the MOU is signed.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      No link issued. The client needs one to read and sign the MOU online.
                    </p>
                    <Button size="sm" variant="outline" disabled={busy === "link"}
                            onClick={() => issueLink(false)}>
                      {busy === "link" && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                      Issue link
                    </Button>
                  </div>
                )
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {isSigned
                    ? "Revoked — the MOU is signed and the link has nothing left to offer."
                    : "Not available for an MOU in this state."}
                </p>
              )}
            </Section>

            {(mou.emails || []).length > 0 && (
              <Section title="Emails" icon={Mail}>
                <ul className="space-y-2">
                  {mou.emails.map((e) => (
                    <li key={e.id} className="rounded-md border border-gray-100 p-2 dark:border-gray-700">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-xs font-medium text-gray-900 dark:text-white">{e.kind_label}</span>
                        <span className="text-[11px] text-gray-400">{fmtDateTime(e.sent_at)}</span>
                      </div>
                      <p className="truncate text-[11px] text-gray-500 dark:text-gray-400" title={e.to}>
                        To {e.to}{e.cc ? ` · cc ${e.cc}` : ""}
                      </p>
                      {e.error ? (
                        <p className="mt-0.5 text-[11px] text-red-600 dark:text-red-400">Failed: {e.error}</p>
                      ) : e.opened_at ? (
                        <p className="mt-0.5 flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400">
                          <Eye className="h-3 w-3" /> Opened {fmtDateTime(e.opened_at)}
                          {e.open_count > 1 ? ` · ${e.open_count}×` : ""}
                        </p>
                      ) : (
                        <p className="mt-0.5 text-[11px] text-gray-400">Not opened yet</p>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-[10px] text-gray-400">
                  Opens are tracked with an image pixel; mail apps that block images will not report one.
                </p>
              </Section>
            )}

            {/* Everything that closes the MOU without a signature. All of it
                is a decision, so all of it is manager-gated together. */}
            {canClose && (statusAllowsSign || canReopen) && (
              <Section title="Other outcomes" icon={Ban}>
                <div className="flex flex-wrap gap-2">
                  {mou.status !== "declined" && !isSigned && mou.status !== "cancelled" && (
                    <Button variant="outline" size="sm" onClick={() => setPanel("decline")}>
                      Client declined
                    </Button>
                  )}
                  {!isSigned && mou.status !== "cancelled" && (
                    <Button variant="outline" size="sm" onClick={() => setPanel("cancel")}>
                      Cancel MOU
                    </Button>
                  )}
                  {canReopen && (
                    <Button variant="outline" size="sm" disabled={busy === "reopen"}
                            onClick={() => act("reopen")}>
                      <RotateCcw className="mr-1 h-3.5 w-3.5" /> Reopen as draft
                    </Button>
                  )}
                </div>
              </Section>
            )}

            {mou.lead && (
              <Section title="Lead">
                <Row label="Name">{mou.lead.name || "—"}</Row>
                <Row label="Stage">{mou.lead.lead_stage || "—"}</Row>
                <Button variant="outline" size="sm" className="mt-2"
                        onClick={() => navigate(`/customers/customerdetails?id=${mou.lead.id}`)}>
                  <ExternalLink className="mr-1 h-3.5 w-3.5" /> Open lead
                </Button>
              </Section>
            )}
          </div>
        </div>
      </div>
    </ContainerCard>
  );
}
